import React, { ChangeEvent, FC } from 'react';

import { convertGeopackageToGeoJSON } from 'store/geopackageToGeoJSONSlice';

import { useAppDispatch } from 'hooks/defaultStoreHooks';
import { useDialog } from 'hooks/useDialog';

import { RaaDialog } from 'raa-react';

import { ModelError } from 'models';

import { Feature } from 'ol';
import GeoJSON, { GeoJSONFeatureCollection } from 'ol/format/GeoJSON';

const readGeoJsonFromFileBlob = (file: File) => {
  return new Promise<string>((resolve, reject) => {
    const fileReader = new FileReader();

    fileReader.onload = () => {
      try {
        const geoJson = fileReader.result as string;
        const features = new GeoJSON().readFeatures(geoJson);
        if (!features.length) {
          reject('Filen saknar geometrier');
        }
        resolve(JSON.parse(geoJson));
      } catch (err) {
        reject('Ogiltigt format på filen');
      }
    };

    fileReader.onerror = () => reject('Kunde inte läsa in filen');
    fileReader.readAsText(file);
  });
};

interface GeometryFileUploadProps {
  converting: (converting: boolean) => void;
  success: (features: Feature[]) => void;
  error?: (message: string) => void;
  labelText?: string;
  showReplaceWarning?: boolean;
  id?: string;
  fileUploadRef: React.RefObject<HTMLInputElement>;
}

export const GeometryFileUploadInput: FC<GeometryFileUploadProps> = ({
  converting,
  error,
  success,
  labelText,
  showReplaceWarning,
  id = 'geometry',
  fileUploadRef
}) => {
  const dispatch = useAppDispatch();
  const { setDialog, unsetDialog } = useDialog();

  const onFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length === 1) {
      const file = event.target.files[0];
      const fileNameSegments = file.name.split('.');
      const fileExtension = fileNameSegments.length ? fileNameSegments[fileNameSegments.length - 1].toLowerCase() : '';

      if (fileExtension === 'json' || fileExtension === 'geojson') {
        try {
          const geoJson = await readGeoJsonFromFileBlob(file);
          fileUploadSuccessful(geoJson);
          return;
        } catch (error: any) {
          fileUploadError(error);
          return;
        }
      }

      if (fileExtension !== 'gpkg') {
        fileUploadError('Ogiltigt filformat. Tillåtna filformat är .json .geojson eller .gpkg.');
        return;
      }

      const data = new FormData();
      data.append('file', file);
      converting(true);
      const response = await dispatch(convertGeopackageToGeoJSON(data));

      if (response.meta.requestStatus === 'rejected') {
        const message = (response.payload as ModelError).message;
        fileUploadError(message);
        return;
      }

      fileUploadSuccessful(response.payload);
    }
  };

  const fileUploadSuccessful = (featureCollection: GeoJSONFeatureCollection): GeoJSONFeatureCollection => {
    const features = new GeoJSON().readFeatures(featureCollection);
    const nonPolygons: Feature[] = features.filter(
      (feature) => feature.getGeometry()?.getType() !== 'Polygon' && feature.getGeometry()?.getType() !== 'MultiPolygon'
    );

    if (nonPolygons.length) {
      setDialog(RaaDialog, {
        header: 'Filen kan inte användas',
        proceed: unsetDialog,
        close: unsetDialog,
        ariaDescribedBy: 'dialog-description',
        children: (
          <p id="dialog-description" className="tw-my-4">
            {'Endast geometrier av typen Polygon eller Multipolygon är tillåtna. Hittade geometrier av typen: ' +
              nonPolygons.map((feature: Feature) => feature.getGeometry()?.getType()).join(', ')}
          </p>
        )
      });
    } else {
      success(featureCollection);
    }

    converting(false);
  };

  const fileUploadError = (message: string) => {
    error && error(message);
    converting(false);
    setDialog(RaaDialog, {
      header: 'Filen kan inte användas',
      proceed: unsetDialog,
      close: unsetDialog,
      ariaDescribedBy: 'dialog-description',
      children: (
        <p id="dialog-description" className="tw-my-4">
          {message}
        </p>
      )
    });
  };

  const fileReplaceWarning = (): void => {
    setDialog(RaaDialog, {
      header: 'Varning',
      close: unsetDialog,
      proceed: () => {
        fileUploadRef?.current?.click();
        unsetDialog();
      },
      ariaDescribedBy: 'dialog-description',
      children: (
        <p id="dialog-description" className="tw-my-4">
          Befintliga ytor kommer att ersättas. Är du säker på att du vill fortsätta?
        </p>
      )
    });
  };

  return (
    <>
      {labelText && (
        <label
          htmlFor={`${id}-fileUpload`}
          onClick={(e) => {
            if (showReplaceWarning) {
              e.preventDefault();
              fileReplaceWarning();
            }
          }}
          className="tw-group tw-inline-block tw-items-center tw-gap-1 tw-rounded tw-px-1 tw-text-raa-orange focus-within:tw-bg-raa-orange focus-within:tw-text-raa-white focus-within:tw-outline-transparent"
        >
          <span className="raa-icon raa-icon-file tw-mr-1 !tw-no-underline"></span>
          <span
            role="button"
            className="tw-break-words tw-text-left focus:tw-outline-transparent group-hover:tw-underline"
            aria-controls={`${id}-fileUpload`}
            tabIndex={0}
          >
            {labelText}
          </span>
        </label>
      )}
      <input
        id={`${id}-fileUpload`}
        data-testid="geometryFileUpload"
        type="file"
        accept=".gpkg,.json,.geojson"
        className="tw-hidden"
        onChange={onFileChange}
        ref={fileUploadRef}
      />
    </>
  );
};
